import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { config } from "../config";
import { useEventsApi } from "../hooks/useEventsApi";
import { useRangeStatusApi } from "../hooks/rangeStatusService";
import type { NumberRangeStatus } from "../shared/NumberRangeStatus";
import {
  type NumberRangeOverviewRow,
  useNumberRangeOverview,
} from "../shared/numberRangeOverview";
import {
  getMissingCategoryLookupMessage,
  isResolvingBandCode,
  normalizeToRisingEventCode,
} from "../shared/eventCodeRange";
import EventCodeStructureMap, {
  type BaseFunctionGroup,
} from "./EventCodeStructureMap";
import ExpectedCodeBandsHint from "./ExpectedCodeBandsHint";

export type NumberRangeOverviewPanelProps = {
  highlightBaseId?: number | null;
  highlightSubId?: number | null;
  initialLookupCode?: string;
  filterCurrentSelectionOnly?: boolean;
  showFillLevels?: boolean;
};

type LookupResult = {
  eventCode: number;
  baseId: number;
  subId: number;
  baseLabel: string;
  subLabel: string;
  baseDigit: number;
  subDigit: number;
};

type FocusTarget = {
  baseId: number;
  subId?: number;
};

function groupRowsByBase(rows: NumberRangeOverviewRow[]): BaseFunctionGroup[] {
  const map = new Map<number, BaseFunctionGroup>();
  for (const row of rows) {
    let group = map.get(row.baseId);
    if (!group) {
      group = {
        baseId: row.baseId,
        baseDigit: row.baseDigit,
        baseLabel: row.baseLabel,
        subs: [],
      };
      map.set(row.baseId, group);
    }
    group.subs.push(row);
  }
  for (const group of map.values()) {
    group.subs.sort((a, b) => a.subDigit - b.subDigit || a.subId - b.subId);
  }
  return [...map.values()].sort((a, b) => a.baseDigit - b.baseDigit || a.baseId - b.baseId);
}

export default function NumberRangeOverviewPanel({
  highlightBaseId,
  highlightSubId,
  initialLookupCode = "",
  filterCurrentSelectionOnly = false,
  showFillLevels = false,
}: NumberRangeOverviewPanelProps) {
  const { callApi } = useEventsApi();
  const { fetchRangeStatus } = useRangeStatusApi();
  const { rows, loading, error, reload } = useNumberRangeOverview(callApi);

  const [fillBySubId, setFillBySubId] = useState<Map<number, NumberRangeStatus>>(new Map());
  const [fillLoading, setFillLoading] = useState(false);
  const [fillError, setFillError] = useState<string | null>(null);
  const [fillLevelsVisible, setFillLevelsVisible] = useState(showFillLevels);

  const [selectionOnly, setSelectionOnly] = useState(filterCurrentSelectionOnly);
  const [lookupInput, setLookupInput] = useState(initialLookupCode);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupResult, setLookupResult] = useState<LookupResult | null>(null);
  const [baseDigitFilter, setBaseDigitFilter] = useState<number | null>(null);
  const [focusTarget, setFocusTarget] = useState<FocusTarget | null>(null);

  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const tileRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());
  const baseSectionRefs = React.useRef<Map<number, HTMLElement>>(new Map());

  React.useEffect(() => {
    if (initialLookupCode) {
      setLookupInput(initialLookupCode);
    }
  }, [initialLookupCode]);

  React.useEffect(() => {
    setFillLevelsVisible(showFillLevels);
  }, [showFillLevels]);

  const loadFillLevels = React.useCallback(
    async (force?: boolean) => {
      if (!showFillLevels || !callApi) {
        setFillBySubId(new Map());
        return;
      }
      setFillLoading(true);
      setFillError(null);
      try {
        const statusRows = await fetchRangeStatus({ force });
        const map = new Map<number, NumberRangeStatus>();
        for (const s of statusRows ?? []) {
          map.set(s.sub_id, s);
        }
        setFillBySubId(map);
      } catch (e) {
        console.error("Failed to load fill levels:", e);
        setFillError("Could not load catalog fill levels.");
        setFillBySubId(new Map());
      } finally {
        setFillLoading(false);
      }
    },
    [showFillLevels, callApi, fetchRangeStatus]
  );

  React.useEffect(() => {
    if (!showFillLevels || !callApi) {
      setFillBySubId(new Map());
      return;
    }
    void loadFillLevels(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFillLevels, Boolean(callApi)]);

  const rowsWithFill = useMemo(() => {
    if (!fillLevelsVisible || fillBySubId.size === 0) {
      return rows;
    }
    return rows.map((row) => {
      const fill = fillBySubId.get(row.subId);
      if (!fill) return row;
      return {
        ...row,
        used: fill.used,
        free: fill.free,
        percentFilled: fill.percent_filled,
      };
    });
  }, [rows, fillBySubId, fillLevelsVisible]);

  const filteredRows = useMemo(() => {
    let list = rowsWithFill;
    if (selectionOnly && highlightBaseId != null) {
      list = list.filter(
        (r) =>
          r.baseId === highlightBaseId &&
          (highlightSubId == null || r.subId === highlightSubId)
      );
    }
    if (baseDigitFilter != null) {
      list = list.filter((r) => r.baseDigit === baseDigitFilter);
    }
    return list;
  }, [rowsWithFill, baseDigitFilter, selectionOnly, highlightBaseId, highlightSubId]);

  const baseGroups = useMemo(() => groupRowsByBase(filteredRows), [filteredRows]);

  const availableBaseDigits = useMemo(
    () => [...new Set(rows.map((r) => r.baseDigit))].sort((a, b) => a - b),
    [rows]
  );

  React.useEffect(() => {
    if (!focusTarget) return;
    const t = window.setTimeout(() => {
      const rowKey = `${focusTarget.baseId}-${focusTarget.subId ?? ""}`;
      const tileEl =
        focusTarget.subId != null ? tileRefs.current.get(rowKey) : undefined;
      const sectionEl = baseSectionRefs.current.get(focusTarget.baseId);
      (tileEl ?? sectionEl)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }, 80);
    return () => window.clearTimeout(t);
  }, [focusTarget, baseGroups]);

  const handleLookup = async () => {
    if (!callApi) return;
    setLookupError(null);
    setLookupResult(null);
    const raw = lookupInput.trim();
    const code = parseInt(raw, 10);
    if (Number.isNaN(code)) {
      setLookupError("Enter a numeric event code.");
      return;
    }
    const lookupCode = normalizeToRisingEventCode(code);
    if (lookupCode < 0) {
      setLookupError("Invalid event code.");
      return;
    }

    setLookupBusy(true);
    try {
      const resolved = await callApi(
        `${config.apiUrl}/api/number-range/resolve-event-code/?event_code=${lookupCode}`,
        "GET"
      );
      if (!resolved?.base_id || !resolved?.sub_id) {
        setLookupError(getMissingCategoryLookupMessage(code, rows));
        return;
      }
      const match = rows.find(
        (r) => r.baseId === resolved.base_id && r.subId === resolved.sub_id
      );
      if (match) {
        setLookupResult({
          eventCode: code,
          baseId: match.baseId,
          subId: match.subId,
          baseLabel: match.baseLabel,
          subLabel: match.subLabel,
          baseDigit: match.baseDigit,
          subDigit: match.subDigit,
        });
        setBaseDigitFilter(match.baseDigit);
        setFocusTarget({ baseId: match.baseId, subId: match.subId });
      } else {
        setLookupError(getMissingCategoryLookupMessage(code, rows));
      }
    } catch (e: unknown) {
      const status =
        e && typeof e === "object" && "status" in e
          ? (e as { status?: number }).status
          : undefined;
      if (status === 404) {
        setLookupError(getMissingCategoryLookupMessage(code, rows));
        return;
      }
      const msg =
        e && typeof e === "object" && "responseBody" in e
          ? String((e as { responseBody?: string }).responseBody)
          : "Lookup failed.";
      setLookupError(msg.length > 200 ? "Lookup failed. Check the code and try again." : msg);
    } finally {
      setLookupBusy(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="caption" color="text.secondary" display="block">
        Each base fixes the thousands digit; each sub the hundreds digit. The event code range is
        shown as rising (↑) and resolving (↓).
        {fillLevelsVisible && " Shaded bar = catalog usage."}
      </Typography>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Find base/sub for a code
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="flex-start">
          <TextField
            size="small"
            label="Event code"
            value={lookupInput}
            onChange={(e) => setLookupInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void handleLookup();
              }
            }}
            disabled={lookupBusy}
            sx={{ minWidth: 200 }}
          />
          <Button
            variant="contained"
            onClick={() => void handleLookup()}
            disabled={lookupBusy}
            startIcon={lookupBusy ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
          >
            Look up
          </Button>
        </Stack>
        {lookupError && (
          <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
            {lookupError}
          </Typography>
        )}
        {lookupResult && (
          <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5, bgcolor: "action.hover" }}>
            <Typography variant="body2" color="success.main" fontWeight={600}>
              {lookupResult.eventCode}
              {isResolvingBandCode(lookupResult.eventCode)
                ? ` (resolving; rising ${lookupResult.eventCode - 10000})`
                : ""}{" "}
              → {lookupResult.baseLabel} / {lookupResult.subLabel}
            </Typography>
            <ExpectedCodeBandsHint
              baseDigit={lookupResult.baseDigit}
              subDigit={lookupResult.subDigit}
              sx={{ mt: 0.75 }}
            />
          </Paper>
        )}
      </Paper>

      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" useFlexGap>
        {(highlightBaseId != null || highlightSubId != null) && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={selectionOnly}
                onChange={(_, c) => setSelectionOnly(c)}
              />
            }
            label="Current selection only"
          />
        )}
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            reload(true);
            void loadFillLevels(true);
          }}
          disabled={loading || fillLoading}
        >
          Refresh
        </Button>
        {showFillLevels && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={fillLevelsVisible}
                onChange={(_, c) => setFillLevelsVisible(c)}
              />
            }
            label="Show fill levels"
          />
        )}
      </Stack>

      {availableBaseDigits.length > 1 && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" alignItems="center" useFlexGap>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            Base digit:
          </Typography>
          <Button
            size="small"
            variant={baseDigitFilter == null ? "contained" : "outlined"}
            onClick={() => setBaseDigitFilter(null)}
          >
            All
          </Button>
          {availableBaseDigits.map((digit) => (
            <Button
              key={digit}
              size="small"
              variant={baseDigitFilter === digit ? "contained" : "outlined"}
              onClick={() =>
                setBaseDigitFilter((prev) => (prev === digit ? null : digit))
              }
            >
              {digit}
            </Button>
          ))}
        </Stack>
      )}

      {fillError && fillLevelsVisible && (
        <Alert severity="warning" variant="outlined">
          {fillError}
          <Button size="small" onClick={() => void loadFillLevels(true)} sx={{ ml: 1 }}>
            Retry
          </Button>
        </Alert>
      )}

      {error && (
        <Alert severity="error">
          {error}
          <Button size="small" onClick={() => reload(true)} sx={{ ml: 1 }}>
            Retry
          </Button>
        </Alert>
      )}

      {loading || (fillLevelsVisible && showFillLevels && fillLoading && fillBySubId.size === 0) ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          ref={scrollContainerRef}
          sx={{
            maxHeight: 520,
            overflow: "auto",
            pr: 0.5,
            p: 1,
            borderRadius: 1,
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "grey.900" : "grey.100",
          }}
        >
          <EventCodeStructureMap
            groups={baseGroups}
            highlightBaseId={highlightBaseId}
            highlightSubId={highlightSubId}
            focusTarget={focusTarget}
            fillFromEventCode={lookupResult?.eventCode ?? null}
            showFillLevels={fillLevelsVisible}
            baseSectionRefs={baseSectionRefs}
            tileRefs={tileRefs}
          />
        </Box>
      )}

      {!loading && rows.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          {baseGroups.length} base functions · {filteredRows.length} sub functions on map
        </Typography>
      )}
    </Stack>
  );
}
